import React, { useEffect, useMemo } from "react";
import { useLang } from "@/App";
import Nav from "@/components/Nav";
import LegalFooter from "@/components/LegalFooter";
import CookieBanner from "@/components/CookieBanner";
import Seo from "@/components/Seo";
import { SERVICE_PAGES, bookingWhatsAppUrl } from "@/lib/content";

const SITE = "https://antrenordecreier.ro";

export default function ServicePage({ pageKey, lang }) {
  const { setLang } = useLang();

  useEffect(() => {
    setLang(lang);
  }, [lang, setLang]);

  const data = SERVICE_PAGES[pageKey][lang];
  const roData = SERVICE_PAGES[pageKey].ro;
  const enData = SERVICE_PAGES[pageKey].en;

  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: data.h1,
        description: data.seoDesc,
        url: `${SITE}${data.path}`,
        serviceType: lang === "ro" ? "Consiliere psihologică online" : "Online psychological counselling",
        areaServed: "Worldwide",
        availableLanguage: ["ro", "en"],
        provider: {
          "@type": "Person",
          name: "Silvana Capris",
          jobTitle: lang === "ro" ? "Psiholog clinician" : "Clinical psychologist",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: data.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  }), [data, lang]);

  const openBooking = () => window.open(bookingWhatsAppUrl(lang, data.eyebrow), "_blank", "noopener,noreferrer");

  return (
    <div className="App" data-testid={`service-page-${pageKey}`}>
      <Seo
        title={data.seoTitle}
        description={data.seoDesc}
        canonical={`${SITE}${data.path}`}
        lang={lang}
        altRo={`${SITE}${roData.path}`}
        altEn={`${SITE}${enData.path}`}
        type="article"
        jsonLd={jsonLd}
      />
      <Nav
        inPage={false}
        homeHref={lang === "ro" ? "/" : "/en"}
        langUrls={{ ro: roData.path, en: enData.path }}
      />

      {/* Hero */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16" data-testid="service-hero">
        <div className="container-x" style={{ maxWidth: 860 }}>
          <div className="eyebrow mb-5" data-testid="service-eyebrow">{data.eyebrow}</div>
          <h1 className="h-display" style={{ fontSize: "clamp(34px, 5vw, 60px)", lineHeight: 1.05 }} data-testid="service-h1">
            {data.h1}
          </h1>
          <p className="mt-7 serif" style={{ fontSize: 20, lineHeight: 1.6, color: "var(--ink-soft)" }} data-testid="service-lead">
            {data.lead}
          </p>
          <div className="mt-9">
            <button className="btn-pill btn-dark" onClick={openBooking} data-testid="service-cta-top">
              {lang === "ro" ? "Programează o ședință" : "Make an appointment"}
            </button>
          </div>
        </div>
      </section>

      {/* Body sections */}
      <section className="pb-8">
        <div className="container-x" style={{ maxWidth: 760 }}>
          {data.sections.map((s, i) => (
            <div key={i} className="mb-12" data-testid={`service-section-${i}`}>
              <div style={{ width: 36, height: 1, background: "var(--gold)", marginBottom: 20 }} />
              <h2 className="serif" style={{ fontSize: "clamp(24px, 3vw, 32px)", color: "var(--ink)", marginBottom: 16 }}>{s.h2}</h2>
              {s.body.map((p, j) => (
                <p key={j} style={{ color: "var(--ink-soft)", fontSize: 17, lineHeight: 1.75, marginBottom: 16 }}>{p}</p>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section" style={{ background: "linear-gradient(180deg,#f4ecd8 0%,#ede2c6 100%)" }} data-testid="service-faq">
        <div className="container-x" style={{ maxWidth: 760 }}>
          <div className="eyebrow mb-3">{lang === "ro" ? "Întrebări frecvente" : "Frequently asked"}</div>
          <h2 className="h-display mb-10" style={{ fontSize: "clamp(26px, 3.4vw, 40px)" }}>
            {lang === "ro" ? "Ce întreabă oamenii" : "What people ask"}
          </h2>
          <div className="space-y-6">
            {data.faq.map((f, i) => (
              <div key={i} data-testid={`service-faq-${i}`} style={{ borderBottom: "1px solid var(--line)", paddingBottom: 20 }}>
                <h3 className="serif" style={{ fontSize: 20, color: "var(--ink)", marginBottom: 8 }}>{f.q}</h3>
                <p style={{ color: "var(--ink-soft)", fontSize: 16, lineHeight: 1.7 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section id="book" className="section" data-testid="service-cta-section">
        <div className="container-x text-center" style={{ maxWidth: 680 }}>
          <h2 className="h-display mb-5" style={{ fontSize: "clamp(28px, 3.6vw, 46px)" }}>{data.ctaHeading}</h2>
          <p className="mb-9" style={{ fontSize: 18, lineHeight: 1.6, color: "var(--ink-soft)" }}>{data.ctaSub}</p>
          <button className="btn-pill btn-dark" onClick={openBooking} data-testid="service-cta-bottom">
            {lang === "ro" ? "Programează o ședință" : "Make an appointment"}
          </button>
        </div>
      </section>

      <LegalFooter />
      <CookieBanner />
    </div>
  );
}
